<!-- CityMap.vue - real map with one dot per report, colored by severity (Figma: Municipality layer / Hotspots) -->
<template>
  <div class="city-map" :class="{ tall }" role="img" :aria-label="$t('city.map.aria')">
    <ol-map ref="olmap" style="height: 100%; width: 100%">
      <ol-view :center="center" :zoom="zoom" />
      <ol-tile-layer><ol-source-osm /></ol-tile-layer>
    </ol-map>
    <!-- Variant A (popup): a small card at the selected dot -->
    <div v-if="popup && selected && pos" class="pop" :class="{ below: pos.y < 190 }" :style="{ left: pos.x + 'px', top: pos.y + 'px' }" role="dialog" :aria-label="selected.title">
      <button type="button" class="pop-x" :aria-label="$t('explore.map.close')" @click="pick(null)">×</button>
      <div class="pop-body">
        <p class="pop-title">{{ selected.title || selected.place }}</p>
        <div class="pop-meta">
          <SeverityLabel :severity="selected.severity" />
          <span v-if="selected.place && selected.title">{{ selected.place }}</span>
        </div>
        <ul v-if="selected.labels && selected.labels.length" class="pop-labels">
          <li v-for="l in selected.labels" :key="l">{{ $label(l) }}</li>
        </ul>
        <router-link v-if="selected.submission_id" class="pop-link" :to="{ name: 'ViewSubmission', params: { lang: $i18n.locale, id: selected.submission_id } }">{{ $t('explore.map.open') }} →</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import SeverityLabel from '@/components/SeverityLabel.vue';
import { fromLonLat } from 'ol/proj';
import { Vector as VectorSource } from 'ol/source';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import { Style, Circle as CircleStyle, Fill, Stroke } from 'ol/style';

const LEVELS = [
  { level: 1, color: '#2ecc71', radius: 7 },
  { level: 2, color: '#f39c12', radius: 8 },
  { level: 3, color: '#c0392b', radius: 10 },
];

export default {
  name: 'CityMap',
  components: { SeverityLabel },
  // popup: show the report card at the dot (Explore). Without it the parent shows the report (city dashboard list).
  props: { points: { type: Array, default: () => [] }, tall: Boolean, popup: Boolean, selectedId: { type: String, default: '' } },
  emits: ['select'],
  data() {
    const source = markRaw(new VectorSource({ features: [] }));
    const styles = {};
    const picked = {};
    LEVELS.forEach((l) => {
      picked[l.level] = markRaw([
        new Style({ image: new CircleStyle({ radius: l.radius + 7, fill: new Fill({ color: 'rgba(65,196,171,0.35)' }) }) }),
        new Style({ image: new CircleStyle({ radius: l.radius + 2, fill: new Fill({ color: l.color }), stroke: new Stroke({ color: '#0b2420', width: 3 }) }) }),
      ]);
      styles[l.level] = markRaw(new Style({ image: new CircleStyle({ radius: l.radius, fill: new Fill({ color: l.color }), stroke: new Stroke({ color: '#ffffff', width: 2 }) }) }));
    });
    return { center: fromLonLat([8.68, 50.11]), zoom: 12, source, styles, picked, current: '', pos: null };
  },
  computed: {
    activeId() { return this.popup ? this.current : this.selectedId; },
    selected() { return this.points.find((p) => p.id === this.activeId) || null; },
  },
  watch: {
    points: { immediate: true, handler: 'draw' },
    activeId() { this.restyle(); this.place(); },
  },
  mounted() {
    const attach = (tries = 0) => {
      const map = this.$refs.olmap && this.$refs.olmap.map;
      if (!map) { if (tries < 40) setTimeout(() => attach(tries + 1), 50); return; }
      const layer = new VectorLayer({ source: this.source, zIndex: 10, style: (f) => (f.get('pid') === this.activeId ? this.picked[f.get('level')] || this.picked[2] : this.styles[f.get('level')] || this.styles[2]) });
      map.addLayer(layer);
      map.on('singleclick', (e) => {
        const hit = map.forEachFeatureAtPixel(e.pixel, (f) => f, { hitTolerance: 8, layerFilter: (l) => l === layer });
        this.pick(hit ? hit.get('pid') : null);
      });
      map.on('pointermove', (e) => { map.getTargetElement().style.cursor = map.hasFeatureAtPixel(e.pixel, { hitTolerance: 8, layerFilter: (l) => l === layer }) ? 'pointer' : ''; });
      map.on('postrender', () => this.place());
      this.layer = layer;
      this.map = map;
    };
    attach();
  },
  beforeUnmount() {
    if (this.map && this.layer) this.map.removeLayer(this.layer);
  },
  methods: {
    pick(id) {
      const same = id && id === this.activeId;
      this.current = same && this.popup ? '' : id || '';
      this.$emit('select', same ? null : this.points.find((p) => p.id === id) || null);
    },
    restyle() { if (this.layer) this.layer.changed(); },
    // pixel position of the selected dot, for the popup
    place() {
      const p = this.selected;
      if (!this.popup || !p || !this.map) { if (this.pos) this.pos = null; return; }
      const f = this.source.getFeatures().find((x) => x.get('pid') === p.id);
      const px = f && this.map.getPixelFromCoordinate(f.getGeometry().getCoordinates());
      if (!px) { this.pos = null; return; }
      const w = this.map.getSize() ? this.map.getSize()[0] : 0;
      const x = Math.round(Math.min(Math.max(px[0], 130), Math.max(w - 130, 130)));
      const y = Math.round(px[1]);
      if (!this.pos || this.pos.x !== x || this.pos.y !== y) this.pos = { x, y };
    },
    draw() {
      const coords = [];
      this.source.clear();
      for (const p of this.points) {
        let [lat, lng] = String(p.geo).split(',').map(Number);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        if (Math.abs(lat) > 90) [lat, lng] = [lng, lat]; // a few old reports store longitude first
        this.source.addFeature(new Feature({ geometry: new Point(fromLonLat([lng, lat])), level: Number(p.severity) || 2, pid: p.id }));
        coords.push([lat, lng]);
      }
      if (coords.length) {
        const lat = coords.reduce((a, c) => a + c[0], 0) / coords.length;
        const lng = coords.reduce((a, c) => a + c[1], 0) / coords.length;
        this.center = fromLonLat([lng, lat]);
        const span = Math.max(Math.max(...coords.map((c) => c[0])) - Math.min(...coords.map((c) => c[0])), (Math.max(...coords.map((c) => c[1])) - Math.min(...coords.map((c) => c[1]))) / 2);
        this.zoom = span > 0.5 ? 9 : span > 0.2 ? 10 : span > 0.08 ? 11 : span > 0.03 ? 12 : 13;
      }
    },
  },
};
</script>

<style scoped>
.city-map { position: relative; }
.pop { position: absolute; z-index: 5; width: 240px; transform: translate(-50%, calc(-100% - 22px)); background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); box-shadow: var(--shadow-floating); }
.pop.below { transform: translate(-50%, 22px); }
.pop::after { content: ""; position: absolute; left: 50%; bottom: -7px; width: 12px; height: 12px; background: var(--color-surface); border-right: 1px solid var(--color-border); border-bottom: 1px solid var(--color-border); transform: translateX(-50%) rotate(45deg); }
.pop.below::after { bottom: auto; top: -7px; transform: translateX(-50%) rotate(225deg); }
.pop-x { position: absolute; top: 4px; right: 4px; width: 32px; height: 32px; border: 0; border-radius: 50%; background: transparent; color: var(--color-secondary); font-size: 20px; cursor: pointer; }
.pop-body { padding: 12px 14px 10px; display: flex; flex-direction: column; gap: 8px; text-align: left; }
.pop-title { margin: 0 28px 0 0; font-weight: 600; font-size: 14px; line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.pop-meta { display: flex; flex-wrap: wrap; gap: 4px 10px; align-items: center; font-size: 12px; color: var(--color-secondary); }
.pop-labels { list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; gap: 4px; }
.pop-labels li { padding: 2px 9px; font-size: 12px; border-radius: var(--radius-pill); background: var(--color-subtle); color: var(--color-text); }
.pop-link { font-weight: 600; font-size: 14px; color: var(--color-primary-text); padding: 4px 0; }
.city-map.tall { height: 560px; }
.city-map { height: 300px; border-radius: 12px; overflow: hidden; background: var(--color-subtle); }
</style>
